import { useCallback, useEffect, useState } from "react";
import { Loader2, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { api, errMsg } from "../lib/api";

const STATUS = { running: "läuft", done: "fertig", cancelled: "abgebrochen", failed: "fehlgeschlagen" };

export const useImportJob = (kind, onFinished) => {
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.importJobActive(kind).then(setJob).catch(() => {}), [kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const j = await api.importJobActive(kind);
        setJob(j);
        if (j?.status !== "running") onFinished?.(j);
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [job?.status, kind, onFinished]);
  const start = async (items, label, scope) => {
    try { setJob(await api.importJobStart({ kind, items, label, scope })); toast.success("Übernahme läuft im Hintergrund"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { job, setJob, start };
};

export const ImportJobStatus = ({ job, setJob, testId }) => {
  if (!job) return null;
  const running = job.status === "running";
  const ok = (job.imported || 0) + (job.already || 0);
  const retry = async () => {
    try { setJob(await api.importJobRetry(job.id)); toast.success("Fehlgeschlagene werden erneut versucht"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-1 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-500">{job.label}</div>
      {running && <Progress value={job.total ? (job.done / job.total) * 100 : 3} className="h-2" />}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
        <span data-testid={`${testId}-summary`}>
          {running && <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />}
          <span className="font-num font-semibold text-slate-900">{ok}/{job.total}</span> übernommen
          {job.already > 0 && ` (davon ${job.already} schon vorhanden)`}
          {job.failed > 0 && <span className="text-amber-800"> · {job.failed} übersprungen</span>} · {STATUS[job.status]}
        </span>
        <div className="flex gap-1">
          {running && <Button variant="ghost" size="sm" onClick={async () => { await api.importJobCancel(job.id); toast("Übernahme wird abgebrochen …"); }}
            data-testid={`${testId}-cancel-btn`} className="h-7 gap-1 text-red-700"><X className="h-3.5 w-3.5" />Abbrechen</Button>}
          {!running && job.failed > 0 && <Button variant="outline" size="sm" onClick={retry} data-testid={`${testId}-retry-btn`} className="h-7 gap-1">
            <RotateCcw className="h-3.5 w-3.5" />Fehlgeschlagene erneut versuchen</Button>}
        </div>
      </div>
      {job.error && <div className="text-sm text-red-700" data-testid={`${testId}-error`}>{job.error}</div>}
      {job.failures?.length > 0 && (
        <details className="text-xs text-amber-900" data-testid={`${testId}-failures`}>
          <summary className="cursor-pointer">Gründe anzeigen</summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {job.failures.map((f, i) => <li key={i}>{f.label ? <b>{f.label}: </b> : null}{f.reason}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};
